import { Link } from "react-router";
import { Compass, CheckCircle2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { formatRelative } from "@/lib/utils";
import type { PortalJourneyListRow } from "./types";

// List-page card for a journey assignment. Compact: title + description +
// progress bar + last-activity timestamp.

type Props = { row: PortalJourneyListRow };

export function JourneyCard({ row }: Props) {
  const done = row.completed_public_tasks;
  const total = row.total_public_tasks;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const isComplete = row.completed_at !== null;

  return (
    <Link
      to={`/portal/journeys/${row.id}`}
      className="group block rounded-xl border bg-card p-5 transition-colors hover:border-foreground/20"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-md bg-muted p-2">
          {isComplete ? (
            <CheckCircle2 className="h-4 w-4 text-status-active" />
          ) : (
            <Compass className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-display text-base font-semibold leading-snug tracking-tight group-hover:underline group-hover:underline-offset-4">
            {row.journey.name}
          </h3>
          {row.journey.description ? (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
              {row.journey.description}
            </p>
          ) : null}

          <div className="mt-4">
            <div className="mb-1.5 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {done} of {total} {total === 1 ? "task" : "tasks"}
              </span>
              <span className="font-medium tabular-nums">{pct}%</span>
            </div>
            <Progress value={pct} className="h-1" />
          </div>

          <p className="mt-3 text-[11px] text-muted-foreground">
            {isComplete && row.completed_at
              ? `Completed ${formatRelative(row.completed_at)}`
              : `Last activity ${formatRelative(row.last_updated_at)}`}
          </p>
        </div>
      </div>
    </Link>
  );
}
